import { useQueryAsUser } from '../../clients/graphql/graphql.hooks';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import TrendingDownOutlinedIcon from '@mui/icons-material/TrendingDownOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import BuildIcon from '@mui/icons-material/Build';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import RuleIcon from '@mui/icons-material/Rule';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Paper,
    Tooltip,
    Typography,
    IconButton,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Tab,
    Tabs,
    Collapse,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CloseIcon from '@mui/icons-material/Close';
import _ from 'lodash';
import { StockSymbolComponent } from "../stocks/StockSymbolComponent";
import { useState, useRef } from 'react';
import React from 'react';
import {
    BalancingDecisionV2,
    balancingConnectionQuery,
    RuleValidationResult,
    ModificationResult,
    DecisionContext,
    autoPilotOrdersQuery,
    cancelSlaveAutoPilotOrdersMutation,
    AutoPilotOrder,
    balancingActivitiesQuery,
    BalancingActivity,
    jediPositionHistoryWithDecisionsQuery,
    JediPositionHistoryDecisionRow,
} from "../../clients/graphql/views/pilots-queries";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DataObjectIcon from '@mui/icons-material/DataObject';
import BalanceIcon from '@mui/icons-material/Balance';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import MemoryIcon from '@mui/icons-material/Memory';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CheckIcon from '@mui/icons-material/Check';
import {
    jediPortfolioPositionsGet,
    JediPortfolioPosition,
    pilotTriggerHistoryQuery,
    TriggerHistoryItem,
} from "../../clients/graphql/components/tables-queries";
import {
    portfolioScopeGet,
    PortfolioData,
    PortfolioScope,
    jediPortfolioOrdersSync,
} from "../../clients/graphql/views/positions-queries";
import { useMutationAsUser, useMutationCoordinate } from "../../clients/graphql/graphql.hooks";
import { jediResolveOverAllocation } from "../../clients/graphql/views/over-allocation-queries";
import BalanceOutlinedIcon from '@mui/icons-material/BalanceOutlined';
import { useCalculatePortfolioHistorySequences } from "../../pages/users/calculatePositionHistorySequencesMutations";

interface Props {
    userKey: number;
    autoPilotSettingsKey: number;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div role="tabpanel" hidden={value !== index} {...other}>
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

const AttributionIcon: React.FC<{ state: string }> = ({ state }) => {
    const iconProps = { sx: { fontSize: 20 } };

    switch (state) {
        case 'NO_WORK_REQUIRED':
            return (
                <Tooltip title="No Work Required" arrow>
                    <CheckCircleIcon {...iconProps} sx={{ ...iconProps.sx, color: '#10b981' }} />
                </Tooltip>
            );
        case 'PLATFORM':
            return (
                <Tooltip title="Platform Issue" arrow>
                    <BuildIcon {...iconProps} sx={{ ...iconProps.sx, color: '#ef4444' }} />
                </Tooltip>
            );
        case 'USER':
            return (
                <Tooltip title="User Action Required" arrow>
                    <PersonIcon {...iconProps} sx={{ ...iconProps.sx, color: '#f59e0b' }} />
                </Tooltip>
            );
        case 'BROKER':
            return (
                <Tooltip title="Broker Issue" arrow>
                    <AccountBalanceIcon {...iconProps} sx={{ ...iconProps.sx, color: '#3b82f6' }} />
                </Tooltip>
            );
        default:
            return (
                <Tooltip title="Unknown" arrow>
                    <InfoIcon {...iconProps} sx={{ ...iconProps.sx, color: '#6b7280' }} />
                </Tooltip>
            );
    }
};

const DecisionIcon: React.FC<{ kind: string }> = ({ kind }) => {
    const isNoop = kind === 'INoopBalancingDecision';
    return isNoop ? (
        <Tooltip title="No Action Needed" arrow>
            <CheckCircleIcon sx={{ fontSize: 20, color: '#10b981' }} />
        </Tooltip>
    ) : (
        <Tooltip title="Order Will Execute" arrow>
            <RocketLaunchIcon sx={{ fontSize: 20, color: '#8b5cf6' }} />
        </Tooltip>
    );
};

interface DetailModalProps {
    open: boolean;
    onClose: () => void;
    decision: any;
    userKey: number;
    autoPilotSettingsKey: number;
    triggerHistory: TriggerHistoryItem[];
}

const DetailModal: React.FC<DetailModalProps> = ({ open, onClose, decision, userKey, autoPilotSettingsKey, triggerHistory }) => {
    const [tabValue, setTabValue] = useState(0);
    const [selectedHistoryRow, setSelectedHistoryRow] = useState<JediPositionHistoryDecisionRow | null>(null);

    const symbol: string | undefined = decision?.symbol || decision?.targetPosition?.symbol;
    const positionType: string | undefined = decision?.positionType || decision?.targetPosition?.type;

    // Only fire when the modal is actually open and we have a symbol to look up.
    const { data: historyData, loading: historyLoading, error: historyError } = useQueryAsUser(
        jediPositionHistoryWithDecisionsQuery,
        {
            userKey,
            variables: {
                autoPilotSettingsKey,
                symbol,
                positionType,
            },
            skip: !open || !symbol || !autoPilotSettingsKey,
            fetchPolicy: 'cache-and-network',
            errorPolicy: 'all',
        },
    );

    const historyRows: JediPositionHistoryDecisionRow[] =
        historyData?.jediPositionHistoryWithDecisions?.rows ?? [];

    if (!decision) return null;

    const rulesPassed: RuleValidationResult[] = decision.rulesPassed || [];
    const rulesFailed: RuleValidationResult[] = decision.rulesFailed || [];
    const modifications: ModificationResult[] = decision.targetPosition?.modifications || [];

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                pb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {decision.symbol}
                    </Typography>
                    <Chip
                        label={decision.kind === 'INoopBalancingDecision' ? 'No Action' : 'Execute'}
                        size="small"
                        sx={{
                            bgcolor: decision.kind === 'INoopBalancingDecision' ? '#dcfce7' : '#fef3c7',
                            color: decision.kind === 'INoopBalancingDecision' ? '#166534' : '#92400e',
                            fontWeight: 600,
                        }}
                    />
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                {/* Summary Stats */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 2,
                    mb: 3
                }}>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Current Qty</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{_.round(decision.currentQuantity, 6)}</Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Target Qty</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>{_.round(decision.targetQuantity, 6)}</Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Execute Qty</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: decision.orderSide === 'BUY' ? '#10b981' : '#ef4444' }}>
                            {decision.orderSide === 'SELL' ? '-' : '+'}{_.round(decision.quantityToExecute, 6)}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                        <Typography variant="caption" color="text.secondary">Amount</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: decision.orderSide === 'BUY' ? '#10b981' : '#ef4444' }}>
                            {decision.amountToExecute?.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Typography>
                    </Box>
                </Box>

                <Tabs
                    value={tabValue}
                    onChange={(_, v) => setTabValue(v)}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 }
                    }}
                >
                    <Tab
                        icon={<RuleIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={`Rules (${rulesPassed.length + rulesFailed.length})`}
                    />
                    <Tab
                        icon={<TuneIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={`Modifications (${modifications.length})`}
                    />
                    <Tab
                        icon={<HistoryIcon sx={{ fontSize: 18 }} />}
                        iconPosition="start"
                        label={
                            historyLoading && historyRows.length === 0
                                ? 'Position History'
                                : `Position History (${historyRows.length})`
                        }
                    />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    {rulesFailed.length > 0 && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle2" sx={{ color: '#ef4444', fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ErrorIcon sx={{ fontSize: 18 }} /> Failed Rules ({rulesFailed.length})
                            </Typography>
                            <List dense sx={{ bgcolor: '#fef2f2', borderRadius: 2 }}>
                                {rulesFailed.map((rule, idx) => (
                                    <ListItem key={idx} sx={{ py: 1 }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.type}</Typography>}
                                            secondary={rule.reason || 'No reason provided'}
                                        />
                                        {rule.workRequiredAttributionState && (
                                            <AttributionIcon state={rule.workRequiredAttributionState} />
                                        )}
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {rulesPassed.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#10b981', fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CheckCircleIcon sx={{ fontSize: 18 }} /> Passed Rules ({rulesPassed.length})
                            </Typography>
                            <List dense sx={{ bgcolor: '#f0fdf4', borderRadius: 2 }}>
                                {rulesPassed.map((rule, idx) => (
                                    <ListItem key={idx} sx={{ py: 1 }}>
                                        <ListItemIcon sx={{ minWidth: 36 }}>
                                            <CheckCircleIcon sx={{ color: '#10b981', fontSize: 20 }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.type}</Typography>}
                                            secondary={rule.reason || 'Passed'}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}

                    {rulesPassed.length === 0 && rulesFailed.length === 0 && (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No rules evaluated
                        </Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {modifications.length > 0 ? (
                        <List sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
                            {modifications.map((mod, idx) => (
                                <ListItem key={idx} sx={{ py: 1.5 }}>
                                    <ListItemIcon sx={{ minWidth: 36 }}>
                                        {mod.modified ? (
                                            <TuneIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                                        ) : (
                                            <PauseCircleIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                                        )}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {mod.type.replace(/Modification$/, '').replace(/([A-Z])/g, ' $1').trim()}
                                                </Typography>
                                                <Chip
                                                    label={mod.modified ? 'Applied' : 'Not Applied'}
                                                    size="small"
                                                    sx={{
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        bgcolor: mod.modified ? '#dbeafe' : '#f3f4f6',
                                                        color: mod.modified ? '#1d4ed8' : '#6b7280',
                                                    }}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                {mod.modified && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        Modified Qty: {_.round(mod.modifiedTargetQuantity, 6)} |
                                                        Modified Value: {((mod.modifiedTargetQuantity || 0) * (decision.price || 0)).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                                    </Typography>
                                                )}
                                                {mod.reason && (
                                                    <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mt: 0.5 }}>
                                                        {mod.reason}
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No modifications applied
                        </Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    {historyLoading && historyRows.length === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : historyError && historyRows.length === 0 ? (
                        <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
                            {historyError.message}
                        </Typography>
                    ) : historyRows.length === 0 ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                            No position history found for {symbol}
                        </Typography>
                    ) : (
                        <PositionHistoryTimeline
                            historyRows={historyRows}
                            triggerHistory={triggerHistory}
                            onViewRow={(r) => setSelectedHistoryRow(r)}
                        />
                    )}
                </TabPanel>
            </DialogContent>
        </Dialog>
        <PositionHistoryDetailsModal
            open={!!selectedHistoryRow}
            row={selectedHistoryRow}
            onClose={() => setSelectedHistoryRow(null)}
        />
        </>
    );
};

// Trigger types whose amount represents money in (+) or money out (−).
const TRIGGER_INFLOW = new Set(['MANUAL_DEPOSIT', 'RECURRING_INVESTMENT']);
const TRIGGER_OUTFLOW = new Set(['MANUAL_WITHDRAW', 'MASTER_DELETED']);

type TimelineEvent =
    | { kind: 'history'; date: number; row: JediPositionHistoryDecisionRow }
    | { kind: 'trigger'; date: number; trigger: TriggerHistoryItem };

const buildTimelineEvents = (
    historyRows: JediPositionHistoryDecisionRow[],
    triggerHistory: TriggerHistoryItem[],
): TimelineEvent[] => {
    const events: TimelineEvent[] = [];
    for (const row of historyRows) {
        const stamp = row.placedAt ?? row.computedAt;
        if (!stamp) continue;
        events.push({ kind: 'history', date: new Date(stamp).getTime(), row });
    }
    for (const trigger of triggerHistory) {
        if (!trigger.createdAt) continue;
        events.push({ kind: 'trigger', date: new Date(trigger.createdAt).getTime(), trigger });
    }
    events.sort((a, b) => b.date - a.date);

    // Collapse runs of MASTER_TRADED triggers between position sequences down to
    // just the most recent one (the trigger closest to the position sequence
    // chronologically after it — which in desc order is the first one seen in
    // each gap).
    const collapsed: TimelineEvent[] = [];
    let masterKeptInGap = false;
    for (const evt of events) {
        if (evt.kind === 'history') {
            masterKeptInGap = false;
            collapsed.push(evt);
            continue;
        }
        if (evt.trigger.type === 'MASTER_TRADED') {
            if (masterKeptInGap) continue;
            masterKeptInGap = true;
        }
        collapsed.push(evt);
    }
    return collapsed;
};

const TRIGGER_TYPE_STYLE: Record<string, { bg: string; fg: string }> = {
    MANUAL_DEPOSIT:        { bg: '#ecfdf5', fg: '#047857' },
    RECURRING_INVESTMENT:  { bg: '#ecfdf5', fg: '#047857' },
    MANUAL_WITHDRAW:       { bg: '#fef2f2', fg: '#b91c1c' },
    MASTER_DELETED:        { bg: '#fef2f2', fg: '#b91c1c' },
    MASTER_TRADED:         { bg: '#fef3c7', fg: '#92400e' },
    REBALANCE:             { bg: '#eff6ff', fg: '#1d4ed8' },
    ALLOCATION_ADJUSTMENT: { bg: '#f3e8ff', fg: '#6b21a8' },
};

const formatTriggerType = (type: string): string =>
    type.split('_').map((s) => s.charAt(0) + s.slice(1).toLowerCase()).join(' ');

const TriggerMarker: React.FC<{ trigger: TriggerHistoryItem }> = ({ trigger }) => {
    const style = TRIGGER_TYPE_STYLE[trigger.type] ?? { bg: '#f3f4f6', fg: '#374151' };
    const sign = TRIGGER_INFLOW.has(trigger.type) ? '+' : TRIGGER_OUTFLOW.has(trigger.type) ? '−' : '';
    const amountColor = sign === '+' ? '#10b981' : sign === '−' ? '#ef4444' : 'text.primary';

    return (
        <Box
            sx={{
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                color: 'text.secondary',
                minHeight: 32,
            }}
        >
            <Chip
                label={formatTriggerType(trigger.type)}
                size="small"
                sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    bgcolor: style.bg,
                    color: style.fg,
                }}
            />
            {trigger.transactionAmount != null && trigger.transactionAmount !== 0 && (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', fontWeight: 600, color: amountColor }}>
                    {sign}{trigger.transactionAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
            )}
            {trigger.tradeCount > 0 && (
                <Typography variant="caption" sx={{ color: 'grey.700' }}>
                    {trigger.tradeCount} trade{trigger.tradeCount === 1 ? '' : 's'}
                </Typography>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {trigger.createdAt ? new Date(trigger.createdAt).toLocaleString() : '—'}
            </Typography>
        </Box>
    );
};

const dotColorForEvent = (evt: TimelineEvent): string => {
    if (evt.kind === 'history') return TRADE_KIND_STYLE[tradeKindOf(evt.row)].fg;
    return TRIGGER_TYPE_STYLE[evt.trigger.type]?.fg ?? '#6b7280';
};

const TimelineDot: React.FC<{ color: string; large: boolean }> = ({ color, large }) => {
    const size = large ? 14 : 9;
    return (
        <Box
            sx={{
                width: size,
                height: size,
                mt: large ? '18px' : '14px',
                borderRadius: '50%',
                bgcolor: color,
                border: '2px solid white',
                boxShadow: `0 0 0 1px ${color}55`,
                justifySelf: 'center',
                zIndex: 1,
            }}
        />
    );
};

// Returns "Today", "Yesterday", or a localized date string.
const dayLabel = (timestamp: number): string => {
    const d = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    const sameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    if (sameDay(d, today)) return 'Today';
    if (sameDay(d, yesterday)) return 'Yesterday';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

const PositionHistoryTimeline: React.FC<{
    historyRows: JediPositionHistoryDecisionRow[];
    triggerHistory: TriggerHistoryItem[];
    onViewRow: (row: JediPositionHistoryDecisionRow) => void;
}> = ({ historyRows, triggerHistory, onViewRow }) => {
    const events = buildTimelineEvents(historyRows, triggerHistory);

    // Compute which rows should render a day-divider above them (first-of-day).
    const dayHeaders: string[] = [];
    let prevDay = '';
    for (const evt of events) {
        const d = dayLabel(evt.date);
        dayHeaders.push(d !== prevDay ? d : '');
        prevDay = d;
    }

    return (
        <Box
            sx={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: '24px 1fr',
                columnGap: 1.5,
                rowGap: 0.25,
                pt: 0.5,
                pb: 1,
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 11,
                    top: 18,
                    bottom: 18,
                    width: 2,
                    bgcolor: 'grey.200',
                    borderRadius: 1,
                    zIndex: 0,
                },
            }}
        >
            {events.map((evt, idx) => {
                const isHistory = evt.kind === 'history';
                const key = isHistory
                    ? `h-${evt.row.sequenceNumber ?? idx}`
                    : `t-${evt.trigger.triggerHistoryKey}`;
                const header = dayHeaders[idx];
                return (
                    <React.Fragment key={key}>
                        {header && (
                            <>
                                <Box />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'grey.500',
                                        fontWeight: 700,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.6,
                                        fontSize: '0.65rem',
                                        mt: idx === 0 ? 0 : 1.5,
                                        mb: 0.25,
                                    }}
                                >
                                    {header}
                                </Typography>
                            </>
                        )}
                        <TimelineDot color={dotColorForEvent(evt)} large={isHistory} />
                        {isHistory ? (
                            <PositionHistoryRow row={evt.row} onView={() => onViewRow(evt.row)} />
                        ) : (
                            <TriggerMarker trigger={evt.trigger} />
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

type TradeKind = 'BUY' | 'SELL' | 'MANUAL' | 'NO_ORDER';

const tradeKindOf = (row: JediPositionHistoryDecisionRow): TradeKind => {
    const hasOrder = row.placedAt != null;
    if (!hasOrder) return 'NO_ORDER';
    if (row.autoPilotSettingsKey == null) return 'MANUAL';
    if (row.orderSide === 'BUY') return 'BUY';
    if (row.orderSide === 'SELL') return 'SELL';
    return 'MANUAL';
};

const TRADE_KIND_STYLE: Record<TradeKind, { bg: string; fg: string; label: string; sign: string }> = {
    BUY:      { bg: '#dcfce7', fg: '#166534', label: 'BUY',      sign: '+' },
    SELL:     { bg: '#fee2e2', fg: '#991b1b', label: 'SELL',     sign: '−' },
    MANUAL:   { bg: '#dbeafe', fg: '#1e40af', label: 'MANUAL',   sign: ''  },
    NO_ORDER: { bg: '#f3f4f6', fg: '#6b7280', label: 'NO ORDER', sign: ''  },
};

const PositionHistoryRow: React.FC<{
    row: JediPositionHistoryDecisionRow;
    onView: () => void;
}> = ({ row, onView }) => {
    const kind = tradeKindOf(row);
    const style = TRADE_KIND_STYLE[kind];
    const isBuy = kind === 'BUY';
    const isSell = kind === 'SELL';
    const amountColor = isBuy ? '#10b981' : isSell ? '#ef4444' : 'text.primary';

    return (
        <Box
            sx={{
                px: 2,
                py: 1.5,
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                bgcolor: 'white',
                display: 'grid',
                gridTemplateColumns: '88px 1fr auto auto',
                alignItems: 'center',
                columnGap: 3,
                transition: 'border-color 120ms ease',
                '&:hover': { borderColor: 'grey.300' },
            }}
        >
            <Box
                sx={{
                    bgcolor: style.bg,
                    color: style.fg,
                    borderRadius: 1.5,
                    py: 0.75,
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    letterSpacing: 0.5,
                }}
            >
                {style.label}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '1rem', fontWeight: 600, color: amountColor }}>
                        {row.quantity != null ? `${style.sign}${_.round(row.quantity, 6)}` : '—'}
                    </Typography>
                    {row.averagePrice != null && (
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'grey.700' }}>
                            @ {fmtUsd(row.averagePrice)}
                        </Typography>
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    #{row.sequenceNumber ?? '–'}
                    {' · '}
                    Net after: <Box component="span" sx={{ fontFamily: 'monospace', color: 'grey.800' }}>{fmtQty(row.totalNetQuantity)}</Box>
                    {row.placedAt ? ` · ${new Date(row.placedAt).toLocaleString()}` : row.computedAt ? ` · ${new Date(row.computedAt).toLocaleString()}` : ''}
                </Typography>
            </Box>

            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.95rem', fontWeight: 700, color: amountColor, textAlign: 'right' }}>
                {fmtUsd(row.amountSpent)}
            </Typography>

            <Button
                variant="outlined"
                size="small"
                onClick={onView}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
            >
                View Position Data
            </Button>
        </Box>
    );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; tone?: 'default' | 'buy' | 'sell' }> = ({ label, value, tone = 'default' }) => {
    const color = tone === 'buy' ? '#10b981' : tone === 'sell' ? '#ef4444' : undefined;
    return (
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                {label}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600, fontFamily: 'monospace', color, mt: 0.25, wordBreak: 'break-all' }}>
                {value}
            </Typography>
        </Box>
    );
};

const fmtUsd = (n: number | null | undefined): string =>
    n == null ? '—' : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const fmtQty = (n: number | null | undefined): string =>
    n == null ? '—' : String(_.round(n, 6));

interface PositionHistoryDetailsModalProps {
    open: boolean;
    onClose: () => void;
    row: JediPositionHistoryDecisionRow | null;
}

const SectionHeader: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
    <Typography
        variant="overline"
        sx={{
            color: 'grey.700',
            fontWeight: 700,
            letterSpacing: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mt: 4,
            mb: 1.5,
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'grey.200',
        }}
    >
        {icon} {label}
    </Typography>
);

const PositionHistoryDetailsModal: React.FC<PositionHistoryDetailsModalProps> = ({ open, onClose, row }) => {
    const [jsonOpen, setJsonOpen] = useState(false);

    if (!row) return null;

    const kind = tradeKindOf(row);
    const style = TRADE_KIND_STYLE[kind];
    const isBuy = kind === 'BUY';
    const isSell = kind === 'SELL';
    const decision = row.decision;
    const symbol = decision?.targetPosition?.symbol;
    const rulesPassed: RuleValidationResult[] = decision?.rulesPassed || [];
    const rulesFailed: RuleValidationResult[] = decision?.rulesFailed || [];
    const modifications: ModificationResult[] = decision?.targetPosition?.modifications || [];
    const tradeTone: 'buy' | 'sell' | 'default' = isBuy ? 'buy' : isSell ? 'sell' : 'default';
    const decisionTone: 'buy' | 'sell' | 'default' =
        decision?.orderSide === 'BUY' ? 'buy' : decision?.orderSide === 'SELL' ? 'sell' : 'default';

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    height: '95vh',
                    maxHeight: '95vh',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                },
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                pb: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Position Data{symbol ? ` · ${symbol}` : ''}
                    </Typography>
                    <Chip
                        label={`#${row.sequenceNumber ?? '–'}`}
                        size="small"
                        sx={{ height: 22, fontSize: '0.7rem', fontFamily: 'monospace', bgcolor: 'grey.100' }}
                    />
                    <Chip
                        label={style.label}
                        size="small"
                        sx={{
                            height: 22,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: style.bg,
                            color: style.fg,
                            letterSpacing: 0.5,
                        }}
                    />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DataObjectIcon sx={{ fontSize: 16 }} />}
                        onClick={() => setJsonOpen(true)}
                        disabled={!decision}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        View JSON
                    </Button>
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                {/* Hero summary */}
                <Box
                    sx={{
                        p: 3,
                        borderRadius: 3,
                        bgcolor: '#f8fafc',
                        border: '1px solid',
                        borderColor: 'grey.200',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 3,
                        alignItems: 'end',
                    }}
                >
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem' }}>
                            Trade Quantity
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 700, color: isBuy ? '#10b981' : isSell ? '#ef4444' : 'text.primary' }}>
                            {row.quantity != null ? `${style.sign}${_.round(row.quantity, 6)}` : '—'}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem' }}>
                            Avg Price
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 700 }}>
                            {fmtUsd(row.averagePrice)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem' }}>
                            Trade Amount
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 700, color: isBuy ? '#10b981' : isSell ? '#ef4444' : 'text.primary' }}>
                            {fmtUsd(row.amountSpent)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.65rem' }}>
                            Net Qty After Trade
                        </Typography>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 700 }}>
                            {fmtQty(row.totalNetQuantity)}
                        </Typography>
                    </Box>
                </Box>

                <SectionHeader icon={<ReceiptLongIcon sx={{ fontSize: 18 }} />} label="Trade Details" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                    <Stat label="Side" value={style.label} tone={tradeTone} />
                    <Stat label="Position Effect" value={row.positionEffect ?? '—'} />
                    <Stat label="Placed At" value={row.placedAt ? new Date(row.placedAt).toLocaleString() : '—'} />
                    <Stat label="Computed At" value={row.computedAt ? new Date(row.computedAt).toLocaleString() : '—'} />
                    <Stat label="Funds Available" value={fmtUsd(row.fundsAvailable)} />
                    <Stat label="Portfolio Key" value={row.portfolioKey ?? '—'} />
                    <Stat label="Asset Key" value={row.assetKey ?? '—'} />
                    <Stat label="AutoPilot Settings Key" value={row.autoPilotSettingsKey ?? 'Manual Trade'} />
                </Box>

                <SectionHeader icon={<BalanceIcon sx={{ fontSize: 18 }} />} label="Decision" />
                {!decision ? (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                        No decision snapshot was matched for this entry.
                    </Typography>
                ) : (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                            <Stat label="Kind" value={decision.kind?.replace(/Balancing.*/, '') ?? '—'} />
                            <Stat label="Side" value={decision.orderSide ?? '—'} tone={decisionTone} />
                            <Stat label="Current Qty" value={fmtQty(decision.currentQuantity)} />
                            <Stat label="Target Qty" value={fmtQty(decision.targetQuantity)} />
                            <Stat label="Execute Qty" value={fmtQty(decision.quantityToExecute)} tone={decisionTone} />
                            <Stat label="Execute Amount" value={fmtUsd(decision.amountToExecute)} tone={decisionTone} />
                            <Stat label="Decision Price" value={fmtUsd(decision.price)} />
                            <Stat label="Position Type" value={decision.positionType ?? '—'} />
                        </Box>

                        {decision.targetPosition && (
                            <>
                                <Typography variant="caption" sx={{ display: 'block', color: 'grey.600', fontWeight: 600, mt: 3, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                    Target Position
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                                    <Stat label="Current Market Value" value={fmtUsd(decision.targetPosition.currentMarketValue)} />
                                    <Stat label="Target Market Value" value={fmtUsd(decision.targetPosition.targetMarketValue)} />
                                    <Stat label="Current Qty" value={fmtQty(decision.targetPosition.currentQuantity)} />
                                    <Stat label="Target Qty" value={fmtQty(decision.targetPosition.targetQuantity)} />
                                    <Stat label="Modified Target Qty" value={fmtQty(decision.targetPosition.modifiedTargetQuantity)} />
                                    <Stat label="Pinned Txn Amount" value={fmtUsd(decision.targetPosition.pinnedTransactionAmount)} />
                                    <Stat label="Pinned Txn Quantity" value={fmtQty(decision.targetPosition.pinnedTransactionQuantity)} />
                                    <Stat label="Price" value={fmtUsd(decision.targetPosition.price)} />
                                </Box>
                            </>
                        )}

                        {modifications.length > 0 && (
                            <>
                                <SectionHeader icon={<TuneIcon sx={{ fontSize: 18 }} />} label={`Modifications (${modifications.length})`} />
                                <List sx={{ bgcolor: 'grey.50', borderRadius: 2 }}>
                                    {modifications.map((mod, idx) => (
                                        <ListItem key={idx} sx={{ py: 1.25 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                {mod.modified ? (
                                                    <TuneIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                                                ) : (
                                                    <PauseCircleIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                                                )}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {mod.type.replace(/Modification$/, '').replace(/([A-Z])/g, ' $1').trim()}
                                                        </Typography>
                                                        <Chip
                                                            label={mod.modified ? 'Applied' : 'Not Applied'}
                                                            size="small"
                                                            sx={{
                                                                height: 20,
                                                                fontSize: '0.7rem',
                                                                bgcolor: mod.modified ? '#dbeafe' : '#f3f4f6',
                                                                color: mod.modified ? '#1d4ed8' : '#6b7280',
                                                            }}
                                                        />
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        {mod.modified && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                Modified Qty: {_.round(mod.modifiedTargetQuantity, 6)} · Modified Value: {fmtUsd((mod.modifiedTargetQuantity || 0) * (decision.price || 0))}
                                                            </Typography>
                                                        )}
                                                        {mod.reason && (
                                                            <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', mt: 0.5 }}>
                                                                {mod.reason}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}

                        {(rulesFailed.length > 0 || rulesPassed.length > 0) && (
                            <SectionHeader
                                icon={<RuleIcon sx={{ fontSize: 18 }} />}
                                label={`Rules · ${rulesPassed.length} passed, ${rulesFailed.length} failed`}
                            />
                        )}
                        <Box sx={{ display: 'grid', gridTemplateColumns: rulesFailed.length > 0 && rulesPassed.length > 0 ? '1fr 1fr' : '1fr', gap: 2 }}>
                            {rulesFailed.length > 0 && (
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#ef4444', fontWeight: 600, mb: 0.75 }}>
                                        <ErrorIcon sx={{ fontSize: 16 }} /> Failed ({rulesFailed.length})
                                    </Typography>
                                    <List dense sx={{ bgcolor: '#fef2f2', borderRadius: 2, py: 0 }}>
                                        {rulesFailed.map((rule, idx) => (
                                            <ListItem key={idx} sx={{ py: 0.75 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <ErrorIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.type}</Typography>}
                                                    secondary={rule.reason || 'No reason provided'}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                            {rulesPassed.length > 0 && (
                                <Box>
                                    <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#10b981', fontWeight: 600, mb: 0.75 }}>
                                        <CheckCircleIcon sx={{ fontSize: 16 }} /> Passed ({rulesPassed.length})
                                    </Typography>
                                    <List dense sx={{ bgcolor: '#f0fdf4', borderRadius: 2, py: 0 }}>
                                        {rulesPassed.map((rule, idx) => (
                                            <ListItem key={idx} sx={{ py: 0.75 }}>
                                                <ListItemIcon sx={{ minWidth: 32 }}>
                                                    <CheckCircleIcon sx={{ color: '#10b981', fontSize: 18 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{rule.type}</Typography>}
                                                    secondary={rule.reason || 'Passed'}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
        <Dialog
            open={jsonOpen}
            onClose={() => setJsonOpen(false)}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, height: '85vh', maxHeight: '85vh' } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'grey.200' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DataObjectIcon sx={{ fontSize: 18 }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Decision JSON</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigator.clipboard?.writeText(JSON.stringify(decision, null, 2))}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Copy
                    </Button>
                    <IconButton onClick={() => setJsonOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <Box
                    component="pre"
                    sx={{
                        bgcolor: '#0f172a',
                        color: '#e2e8f0',
                        border: '1px solid',
                        borderColor: 'grey.800',
                        borderRadius: 2,
                        p: 2,
                        m: 0,
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        lineHeight: 1.5,
                        height: '100%',
                        overflow: 'auto',
                        whiteSpace: 'pre',
                    }}
                >
                    {JSON.stringify(decision, null, 2)}
                </Box>
            </DialogContent>
        </Dialog>
        </>
    );
};

// Order Row Component with expandable activities
interface OrderRowProps {
    order: AutoPilotOrder;
    onCancel?: (order: AutoPilotOrder) => void;
    cancelling?: boolean;
}

const OrderRow: React.FC<OrderRowProps> = ({ order, onCancel, cancelling }) => {
    const [expanded, setExpanded] = useState(false);
    const activities = order.autoPilotOrderActivities || [];
    const isCancellable = order.orderState === 'PENDING' && !!order.autoPilotOrderKey && !!onCancel;

    const getStatusColor = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'FULFILLED': return { bg: '#f0fdf4', color: '#10b981', border: '#bbf7d0' };
            case 'FAILED': return { bg: '#fef2f2', color: '#ef4444', border: '#fecaca' };
            case 'PENDING': return { bg: '#fef3c7', color: '#d97706', border: '#fcd34d' };
            case 'CANCELLED': return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
            default: return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
        }
    };

    const statusColors = getStatusColor(order.orderStatus);
    const isBuy = order.orderSide === 'BUY';

    return (
        <Box sx={{ mb: 1 }}>
            <Box
                onClick={() => activities.length > 0 && setExpanded(!expanded)}
                sx={{
                    p: 2,
                    bgcolor: 'white',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    cursor: activities.length > 0 ? 'pointer' : 'default',
                    transition: 'all 0.15s ease',
                    '&:hover': activities.length > 0 ? {
                        borderColor: 'grey.300',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    } : {},
                }}
            >
                {/* Expand/Collapse Icon */}
                <Box sx={{ width: 24, display: 'flex', justifyContent: 'center' }}>
                    {activities.length > 0 ? (
                        expanded ? (
                            <KeyboardArrowUpIcon sx={{ color: 'grey.500', fontSize: 20 }} />
                        ) : (
                            <KeyboardArrowDownIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                        )
                    ) : (
                        <Box sx={{ width: 20 }} />
                    )}
                </Box>

                {/* Symbol & Asset */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 120 }}>
                    {order.asset?.pictureUrl && (
                        <Box
                            component="img"
                            src={order.asset.pictureUrl}
                            sx={{ width: 28, height: 28, borderRadius: 1 }}
                        />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {order.asset?.symbol}
                    </Typography>
                </Box>

                {/* Order Side */}
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 80,
                }}>
                    {isBuy ? (
                        <TrendingUpOutlinedIcon sx={{ fontSize: 18, color: '#10b981' }} />
                    ) : (
                        <TrendingDownOutlinedIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                    )}
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isBuy ? '#10b981' : '#ef4444' }}>
                        {order.orderSide}
                    </Typography>
                </Box>

                {/* Amount */}
                <Box sx={{ minWidth: 100 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                        {(order.transactionAmount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </Typography>
                </Box>

                {/* Quantity */}
                <Box sx={{ minWidth: 80 }}>
                    <Typography variant="caption" color="text.secondary">Qty</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {(order.quantity || 0).toFixed(6)}
                    </Typography>
                </Box>

                {/* Balancing Decision Quantity */}
                <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">Bal Dec Qty</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {(order.balancingDecisionQuantity || 0).toFixed(6)}
                    </Typography>
                </Box>

                {/* Status */}
                <Box sx={{ flex: 1 }}>
                    <Chip
                        label={order.orderStatus}
                        size="small"
                        sx={{
                            bgcolor: statusColors.bg,
                            color: statusColors.color,
                            border: `1px solid ${statusColors.border}`,
                            fontWeight: 600,
                            fontSize: '0.75rem',
                        }}
                    />
                </Box>

                {/* State */}
                <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">{order.orderState}</Typography>
                </Box>

                {/* Created At */}
                <Box sx={{ minWidth: 160 }}>
                    <Typography variant="caption" color="text.secondary">Created</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: order.createdAt ? 'grey.700' : 'grey.400' }}>
                        {order.createdAt ? new Date(order.createdAt).toLocaleString(undefined, { timeZoneName: 'short' }) : '—'}
                    </Typography>
                </Box>

                {/* Activities Count */}
                {activities.length > 0 && (
                    <Chip
                        icon={<HistoryIcon sx={{ fontSize: 14 }} />}
                        label={activities.length}
                        size="small"
                        sx={{
                            bgcolor: 'grey.100',
                            color: 'grey.600',
                            fontWeight: 600,
                            '& .MuiChip-icon': { color: 'grey.500' }
                        }}
                    />
                )}

                {/* Cancel Pending Order */}
                {isCancellable && (
                    <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={cancelling}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCancel(order);
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
                    >
                        {cancelling ? 'Cancelling…' : 'Cancel'}
                    </Button>
                )}
            </Box>

            {/* Failure Reason */}
            {order.failureReason && (
                <Box sx={{ mt: 0.5, ml: 5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'grey.500', fontSize: '0.7rem' }}>
                        {typeof order.failureReason === 'object' ? JSON.stringify(order.failureReason) : order.failureReason}
                    </Typography>
                </Box>
            )}

            {/* Expandable Activities */}
            <Collapse in={expanded}>
                <Box sx={{ ml: 5, mt: 1, mb: 1 }}>
                    {activities.map((activity, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                p: 1.5,
                                mb: 0.5,
                                bgcolor: 'grey.50',
                                borderRadius: 1.5,
                                borderLeft: '3px solid',
                                borderLeftColor: activity.errorMessage ? '#ef4444' : '#10b981',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip
                                    label={activity.activityState}
                                    size="small"
                                    sx={{
                                        height: 20,
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        bgcolor: activity.errorMessage ? '#fef2f2' : '#f0fdf4',
                                        color: activity.errorMessage ? '#ef4444' : '#10b981',
                                    }}
                                />
                                {activity.createdAt && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccessTimeIcon sx={{ fontSize: 12 }} />
                                        {new Date(activity.createdAt).toLocaleString()}
                                    </Typography>
                                )}
                            </Box>
                            {activity.value && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'grey.700' }}>
                                    Value: {typeof activity.value === 'object' ? JSON.stringify(activity.value) : activity.value}
                                </Typography>
                            )}
                            {activity.data && (
                                <Box
                                    component="pre"
                                    sx={{
                                        mt: 1,
                                        p: 1.5,
                                        bgcolor: 'grey.900',
                                        color: '#60a5fa',
                                        borderRadius: 1,
                                        fontSize: '0.7rem',
                                        fontFamily: 'monospace',
                                        overflow: 'auto',
                                        maxHeight: 200,
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {typeof activity.data === 'object'
                                        ? JSON.stringify(activity.data, null, 2)
                                        : (() => {
                                            try {
                                                return JSON.stringify(JSON.parse(activity.data), null, 2);
                                            } catch {
                                                return activity.data;
                                            }
                                        })()
                                    }
                                </Box>
                            )}
                            {activity.errorMessage && (
                                <Typography variant="caption" sx={{ display: 'block', color: 'grey.500', fontSize: '0.7rem', mt: 0.5 }}>
                                    {typeof activity.errorMessage === 'object' ? JSON.stringify(activity.errorMessage) : activity.errorMessage}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            </Collapse>
        </Box>
    );
};

// Orders Modal Component
const DEFAULT_CANCEL_REASON = 'Cancelled by customer support';
const MIN_CANCEL_REASON_LENGTH = 10;

interface OrdersModalProps {
    open: boolean;
    onClose: () => void;
    userKey: number;
    autoPilotSettingsKey: number;
}

const OrdersModal: React.FC<OrdersModalProps> = ({ open, onClose, userKey, autoPilotSettingsKey }) => {
    const [limit, setLimit] = useState(50);
    const [offset, setOffset] = useState(0);
    const [symbolFilter, setSymbolFilter] = useState('');

    const { data, loading, error, refetch } = useQueryAsUser(autoPilotOrdersQuery, {
        userKey,
        variables: { autoPilotSettingsKey, userKey, limit, offset },
        skip: !open,
    });

    const [cancelOrders, { loading: cancelling }] = useMutationAsUser(cancelSlaveAutoPilotOrdersMutation, { userKey });
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [orderToCancel, setOrderToCancel] = useState<AutoPilotOrder | null>(null);
    const [cancelReason, setCancelReason] = useState('');

    const trimmedReason = cancelReason.trim();
    const reasonIsValid = trimmedReason.length === 0 || trimmedReason.length >= MIN_CANCEL_REASON_LENGTH;

    const handleCancelClick = (order: AutoPilotOrder) => {
        setCancelReason('');
        setCancelError(null);
        setOrderToCancel(order);
    };

    const handleConfirmCancel = async () => {
        if (!orderToCancel || !reasonIsValid) return;
        setCancelError(null);
        try {
            const result = await cancelOrders({
                variables: {
                    userKey,
                    autoPilotOrderKeys: [orderToCancel.autoPilotOrderKey],
                    reason: trimmedReason.length > 0 ? trimmedReason : DEFAULT_CANCEL_REASON,
                },
            });
            const cancelledKeys = result.data?.slaveAutoPilotOrdersCancel?.cancelledAutoPilotOrderKeys || [];
            if (cancelledKeys.length === 0) {
                setCancelError('Order was not cancelled — it may no longer be in PENDING state.');
            }
            await refetch();
        } catch (e: any) {
            setCancelError(e?.message || 'Failed to cancel order');
        } finally {
            setOrderToCancel(null);
            setCancelReason('');
        }
    };

    const orders: AutoPilotOrder[] = data?.slaveAutoPilotOrdersGet || [];

    // Filter by symbol
    const filteredOrders = symbolFilter
        ? orders.filter(o => o.asset?.symbol?.toLowerCase().includes(symbolFilter.toLowerCase()))
        : orders;

    const handlePrevPage = () => {
        setOffset(Math.max(0, offset - limit));
    };

    const handleNextPage = () => {
        if (orders.length === limit) {
            setOffset(offset + limit);
        }
    };

    // Reset offset when limit changes
    const handleLimitChange = (newLimit: number) => {
        setLimit(newLimit);
        setOffset(0);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={false}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    width: '90vw',
                    maxWidth: '1400px',
                    height: '85vh',
                    maxHeight: '900px',
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                color: 'white',
                py: 2,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ReceiptLongIcon sx={{ fontSize: 28 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.02em' }}>
                        AutoPilot Orders
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* Controls Bar */}
            <Box sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                bgcolor: 'white',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
            }}>
                {/* Symbol Filter */}
                <TextField
                    size="small"
                    placeholder="Filter by symbol..."
                    value={symbolFilter}
                    onChange={(e) => setSymbolFilter(e.target.value)}
                    sx={{ width: 200 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 20, color: 'grey.400' }} />
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Limit Selector */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Limit</InputLabel>
                    <Select
                        value={limit}
                        label="Limit"
                        onChange={(e) => handleLimitChange(e.target.value as number)}
                    >
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={250}>250</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                    </Select>
                </FormControl>

                <Box sx={{ flex: 1 }} />

                {/* Pagination */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {offset + 1} - {offset + orders.length}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handlePrevPage}
                        disabled={offset === 0}
                        sx={{ bgcolor: 'grey.100' }}
                    >
                        <ChevronLeftIcon />
                    </IconButton>
                    <IconButton
                        size="small"
                        onClick={handleNextPage}
                        disabled={orders.length < limit}
                        sx={{ bgcolor: 'grey.100' }}
                    >
                        <ChevronRightIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Cancel Error */}
            {cancelError && (
                <Box sx={{ px: 3, py: 1, bgcolor: '#fef2f2', borderBottom: '1px solid', borderColor: '#fecaca', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                    <Typography variant="body2" sx={{ color: '#ef4444' }}>{cancelError}</Typography>
                </Box>
            )}

            <DialogContent sx={{ p: 0, bgcolor: 'grey.50' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                        <CircularProgress size={48} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <ErrorIcon sx={{ fontSize: 48, color: '#ef4444', mb: 2 }} />
                        <Typography color="error">{error.message}</Typography>
                    </Box>
                ) : filteredOrders.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%', gap: 2 }}>
                        <ReceiptLongIcon sx={{ fontSize: 64, color: 'grey.300' }} />
                        <Typography variant="h6" color="text.secondary">
                            {symbolFilter ? `No orders found for "${symbolFilter}"` : 'No orders found'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
                        {filteredOrders.map((order, idx) => (
                            <OrderRow
                                key={order.autoPilotOrderKey || idx}
                                order={order}
                                onCancel={handleCancelClick}
                                cancelling={cancelling}
                            />
                        ))}
                    </Box>
                )}
            </DialogContent>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={!!orderToCancel}
                onClose={() => !cancelling && setOrderToCancel(null)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon sx={{ color: '#d97706' }} />
                    Cancel AutoPilot Order?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        You are about to cancel the PENDING {orderToCancel?.orderSide} order for{' '}
                        <strong>{orderToCancel?.asset?.symbol}</strong>. This cannot be undone.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        minRows={2}
                        label="Reason for cancelling"
                        placeholder="Why is this order being cancelled?"
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        error={!reasonIsValid}
                        helperText={
                            !reasonIsValid
                                ? `Reason must be at least ${MIN_CANCEL_REASON_LENGTH} characters (or leave empty to use the default)`
                                : `Leave empty to use "${DEFAULT_CANCEL_REASON}"`
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setOrderToCancel(null)} disabled={cancelling} sx={{ textTransform: 'none' }}>
                        Keep Order
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmCancel}
                        disabled={!reasonIsValid || cancelling}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {cancelling ? 'Cancelling…' : 'Cancel Order'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Dialog>
    );
};

// Remote Positions Modal Component
interface RemotePositionsModalProps {
    open: boolean;
    onClose: () => void;
    userKey: number;
    portfolioKey: number;
}

interface PositionSummary {
    id: string;
    symbol: string;
    remoteQuantity: number | null;
    calculatedQuantity: number;
    delta: number | null;
    hasDelta: boolean;
    remoteUnavailable: boolean;
}

// Fun animated icon for sync status
const SyncStatusIcon: React.FC<{ status: 'idle' | 'syncing' | 'recalculating' | 'reloading' | 'complete' | 'error' }> = ({ status }) => {
    const spinKeyframes = {
        '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
        },
    };
    const pulseKeyframes = {
        '@keyframes pulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.15)', opacity: 0.8 },
        },
    };
    const glowKeyframes = {
        '@keyframes glow': {
            '0%, 100%': { filter: 'brightness(1)' },
            '50%': { filter: 'brightness(1.4)' },
        },
    };
    const bounceDownKeyframes = {
        '@keyframes bounceDown': {
            '0%, 100%': { transform: 'translateY(-2px)' },
            '50%': { transform: 'translateY(2px)' },
        },
    };
    const popKeyframes = {
        '@keyframes pop': {
            '0%': { transform: 'scale(0)', opacity: 0 },
            '50%': { transform: 'scale(1.3)', opacity: 1 },
            '100%': { transform: 'scale(1)', opacity: 1 },
        },
    };

    switch (status) {
        case 'syncing':
            return (
                <AutorenewIcon
                    sx={{
                        fontSize: 16,
                        color: '#3b82f6',
                        ...spinKeyframes,
                        animation: 'spin 1s linear infinite',
                    }}
                />
            );
        case 'recalculating':
            return (
                <MemoryIcon
                    sx={{
                        fontSize: 16,
                        color: '#8b5cf6',
                        ...pulseKeyframes,
                        ...glowKeyframes,
                        animation: 'pulse 0.8s ease-in-out infinite, glow 0.8s ease-in-out infinite',
                    }}
                />
            );
        case 'reloading':
            return (
                <CloudDownloadIcon
                    sx={{
                        fontSize: 16,
                        color: '#06b6d4',
                        ...bounceDownKeyframes,
                        animation: 'bounceDown 0.5s ease-in-out infinite',
                    }}
                />
            );
        case 'complete':
            return (
                <CheckIcon
                    sx={{
                        fontSize: 16,
                        color: '#10b981',
                        ...popKeyframes,
                        animation: 'pop 0.4s ease-out forwards',
                    }}
                />
            );
        case 'error':
            return <ErrorIcon sx={{ fontSize: 16, color: '#ef4444' }} />;
        default:
            return <SyncIcon sx={{ fontSize: 16 }} />;
    }
};

const RemotePositionsModal: React.FC<RemotePositionsModalProps> = ({ open, onClose, userKey, portfolioKey }) => {
    const [filter, setFilter] = useState('');
    const [showOnlyDeltas, setShowOnlyDeltas] = useState(false);
    const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'recalculating' | 'reloading' | 'complete' | 'error'>('idle');
    const [syncError, setSyncError] = useState<string | null>(null);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Fetch remote positions
    const { data: remoteData, loading: remoteLoading, error: remoteError, refetch: refetchRemote } = useQueryAsUser(jediPortfolioPositionsGet, {
        userKey,
        portfolioKey,
        variables: { userKey, portfolioKey },
        skip: !open,
    });

    // Fetch calculated positions (ENTIRE_ACCOUNT only)
    const { data: calcData, loading: calcLoading, error, refetch: refetchCalc } = useQueryAsUser(portfolioScopeGet, {
        userKey,
        variables: {
            portfolioKey,
            types: ['ENTIRE_ACCOUNT']
        },
        skip: !open,
    });

    // Mutations for sync and recalculate
    const [syncOrders] = useMutationAsUser(jediPortfolioOrdersSync, { userKey });
    const { mutate: recalculatePositions } = useCalculatePortfolioHistorySequences(
        userKey.toString(),
        portfolioKey.toString()
    );

    const handleSyncAndRecalculate = async () => {
        setShowConfirmDialog(false);
        setSyncStatus('syncing');
        setSyncError(null);

        try {
            // Step 1: Sync orders (global=false)
            await syncOrders({
                variables: {
                    portfolioKeys: [portfolioKey],
                    global: false,
                },
            });

            // Step 2: Recalculate positions
            setSyncStatus('recalculating');
            await recalculatePositions();

            // Step 3: Refetch data to reload the modal
            setSyncStatus('reloading');
            await Promise.all([refetchRemote(), refetchCalc()]);

            // Step 4: Complete
            setSyncStatus('complete');

            // Reset status after a short delay
            setTimeout(() => setSyncStatus('idle'), 2000);
        } catch (err: any) {
            setSyncStatus('error');
            setSyncError(err?.message || 'An error occurred');
        }
    };

    const loading = remoteLoading || calcLoading;

    const remoteUnavailable = !!remoteError;

    // Build position summaries
    const positionSummaries = React.useMemo(() => {
        const remotePositions: JediPortfolioPosition[] = remoteData?.jediPortfolioPositionsGet?.positions || [];
        const portfolioData: PortfolioData = calcData?.portfolioGet?.portfolio;
        const symbolGroups = new Map<string, PositionSummary>();

        // Add remote positions (only if no error)
        if (!remoteUnavailable) {
            remotePositions.forEach(pos => {
                if (!pos.symbol) return;
                const remoteQty = pos.remotePosition?.quantity ?? 0;
                symbolGroups.set(pos.symbol, {
                    id: pos.symbol,
                    symbol: pos.symbol,
                    remoteQuantity: remoteQty,
                    calculatedQuantity: 0,
                    delta: 0,
                    hasDelta: false,
                    remoteUnavailable: false,
                });
            });
        }

        // Add calculated positions (ENTIRE_ACCOUNT)
        if (portfolioData?.portfolioScope) {
            portfolioData.portfolioScope.forEach((scope: PortfolioScope) => {
                scope.positionHistory?.forEach(history => {
                    const symbol = history.position.symbol;
                    if (!symbol) return;
                    const calcQty = history.totalNetQuantity;

                    if (!symbolGroups.has(symbol)) {
                        symbolGroups.set(symbol, {
                            id: symbol,
                            symbol,
                            remoteQuantity: remoteUnavailable ? null : 0,
                            calculatedQuantity: calcQty,
                            delta: null,
                            hasDelta: false,
                            remoteUnavailable,
                        });
                    } else {
                        const summary = symbolGroups.get(symbol)!;
                        summary.calculatedQuantity = calcQty;
                    }
                });
            });
        }

        // Calculate deltas and filter out zeros
        const results: PositionSummary[] = [];
        symbolGroups.forEach(summary => {
            if (summary.remoteQuantity !== null) {
                summary.delta = summary.remoteQuantity - summary.calculatedQuantity;
                summary.hasDelta = Math.abs(summary.delta) >= 0.000001;
            }
            // Only include if remote > 0 OR calculated != 0 OR remote unavailable
            if ((summary.remoteQuantity !== null && summary.remoteQuantity > 0) || summary.calculatedQuantity !== 0 || summary.remoteUnavailable) {
                results.push(summary);
            }
        });

        return results;
    }, [remoteData, calcData, remoteUnavailable]);

    // Filter positions
    const filteredPositions = React.useMemo(() => {
        return positionSummaries.filter(pos => {
            const matchesSearch = pos.symbol.toLowerCase().includes(filter.toLowerCase());
            const matchesDeltaFilter = !showOnlyDeltas || pos.hasDelta;
            return matchesSearch && matchesDeltaFilter;
        });
    }, [positionSummaries, filter, showOnlyDeltas]);

    const rows = _.orderBy(filteredPositions, ['hasDelta', 'symbol'], ['desc', 'asc']);
    const deltaCount = positionSummaries.filter(p => p.hasDelta).length;

    // Detect possible account transfer: all remote positions are 0 but we have calculated positions
    const possibleAccountTransfer = React.useMemo(() => {
        if (remoteUnavailable) return false;
        const positionsWithCalculated = positionSummaries.filter(p => p.calculatedQuantity !== 0);
        if (positionsWithCalculated.length === 0) return false;
        // Check if ALL remote positions for symbols with calculated positions are 0
        return positionsWithCalculated.every(p => p.remoteQuantity === 0);
    }, [positionSummaries, remoteUnavailable]);

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '80vh',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                }
            }}
        >
            {/* Header */}
            <Box sx={{
                px: 3,
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'grey.200',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <StorageIcon sx={{ fontSize: 22, color: '#475569' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                            Position Comparison
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Sync orders from broker and recalculate positions" arrow>
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SyncStatusIcon status={syncStatus} />}
                                onClick={() => setShowConfirmDialog(true)}
                                disabled={syncStatus === 'syncing' || syncStatus === 'recalculating' || syncStatus === 'reloading'}
                                sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    borderColor: syncStatus === 'complete' ? '#10b981' : syncStatus === 'error' ? '#ef4444' : syncStatus === 'syncing' ? '#3b82f6' : syncStatus === 'recalculating' ? '#8b5cf6' : syncStatus === 'reloading' ? '#06b6d4' : '#cbd5e1',
                                    color: syncStatus === 'complete' ? '#10b981' : syncStatus === 'error' ? '#ef4444' : syncStatus === 'syncing' ? '#3b82f6' : syncStatus === 'recalculating' ? '#8b5cf6' : syncStatus === 'reloading' ? '#06b6d4' : '#64748b',
                                    bgcolor: syncStatus === 'complete' ? '#ecfdf5' : syncStatus === 'error' ? '#fef2f2' : syncStatus === 'syncing' ? '#eff6ff' : syncStatus === 'recalculating' ? '#f5f3ff' : syncStatus === 'reloading' ? '#ecfeff' : 'transparent',
                                    '&:hover': {
                                        borderColor: '#94a3b8',
                                        bgcolor: '#f8fafc',
                                    },
                                }}
                            >
                                {syncStatus === 'idle' && 'Sync & Recalculate'}
                                {syncStatus === 'syncing' && 'Syncing Orders...'}
                                {syncStatus === 'recalculating' && 'Recalculating...'}
                                {syncStatus === 'reloading' && 'Fetching Positions...'}
                                {syncStatus === 'complete' && 'Complete!'}
                                {syncStatus === 'error' && 'Error'}
                            </Button>
                        </Tooltip>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'grey.500' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                        label={`${rows.length} positions`}
                        size="small"
                        sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 500 }}
                    />
                    {!loading && deltaCount > 0 && (
                        <Chip
                            label={`${deltaCount} with differences`}
                            size="small"
                            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }}
                        />
                    )}
                    {!loading && possibleAccountTransfer && (
                        <Tooltip
                            title="All remote positions are zero while we have calculated positions. This MAY be due to the user transferring their account to another broker. Please confirm with the user."
                            arrow
                            placement="bottom"
                        >
                            <Chip
                                icon={<ErrorIcon sx={{ fontSize: 16 }} />}
                                label="Possible Account Transfer"
                                size="small"
                                sx={{
                                    bgcolor: '#fef2f2',
                                    color: '#dc2626',
                                    fontWeight: 500,
                                    cursor: 'help',
                                    '& .MuiChip-icon': { color: '#dc2626' }
                                }}
                            />
                        </Tooltip>
                    )}
                    {syncError && (
                        <Chip
                            label={syncError}
                            size="small"
                            onDelete={() => setSyncError(null)}
                            sx={{ bgcolor: '#fef2f2', color: '#dc2626', fontWeight: 500 }}
                        />
                    )}
                </Box>
            </Box>

            {/* Search & Filter */}
            <Box sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'grey.100', bgcolor: 'white' }}>
                <TextField
                    size="small"
                    fullWidth
                    placeholder="Search symbol..."
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 18, color: 'grey.400' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            bgcolor: '#f8fafc',
                            '& fieldset': { borderColor: '#e2e8f0' },
                        }
                    }}
                />
                {deltaCount > 0 && (
                    <Box sx={{ mt: 1.5 }}>
                        <Chip
                            label="Show only differences"
                            size="small"
                            onClick={() => setShowOnlyDeltas(!showOnlyDeltas)}
                            sx={{
                                cursor: 'pointer',
                                bgcolor: showOnlyDeltas ? '#3b82f6' : '#f1f5f9',
                                color: showOnlyDeltas ? 'white' : '#64748b',
                                fontWeight: 500,
                                '&:hover': { bgcolor: showOnlyDeltas ? '#2563eb' : '#e2e8f0' },
                            }}
                        />
                    </Box>
                )}
            </Box>

            {/* Content */}
            <DialogContent sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 250 }}>
                        <CircularProgress size={36} sx={{ color: '#3b82f6' }} />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <ErrorIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
                        <Typography color="error" variant="body2">{error.message}</Typography>
                    </Box>
                ) : rows.length === 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 200, gap: 1 }}>
                        <StorageIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                        <Typography color="text.secondary" variant="body2">
                            {filter ? `No matches for "${filter}"` : 'No positions'}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {/* Column Headers */}
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: '100px 1fr 1fr 100px',
                            gap: 2,
                            px: 3,
                            py: 1.5,
                            bgcolor: '#f8fafc',
                            borderBottom: '1px solid',
                            borderColor: 'grey.200',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Symbol
                            </Typography>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                Remote
                            </Typography>
                            <Tooltip title="Our calculated position based on order history and corporate actions of what they should have in their broker" arrow placement="top">
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, cursor: 'help' }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Calculated
                                    </Typography>
                                    <InfoIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
                                </Box>
                            </Tooltip>
                            <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                Delta
                            </Typography>
                        </Box>

                        {/* Rows */}
                        {rows.map((row) => (
                            <Box
                                key={row.id}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '100px 1fr 1fr 100px',
                                    gap: 2,
                                    px: 3,
                                    py: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'grey.100',
                                    bgcolor: row.hasDelta ? '#fffbeb' : 'white',
                                    '&:hover': { bgcolor: row.hasDelta ? '#fef3c7' : '#f8fafc' },
                                    transition: 'background-color 0.15s',
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                                    {row.symbol}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: row.remoteUnavailable ? 'inherit' : 'monospace',
                                        color: row.remoteUnavailable ? '#ef4444' : '#475569',
                                        textAlign: 'right',
                                        fontStyle: row.remoteUnavailable ? 'italic' : 'normal',
                                        fontSize: row.remoteUnavailable ? '0.75rem' : 'inherit',
                                    }}
                                >
                                    {row.remoteUnavailable ? 'Unavailable' : _.round(row.remoteQuantity ?? 0, 6)}
                                </Typography>
                                <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#475569', textAlign: 'right' }}>
                                    {_.round(row.calculatedQuantity, 6)}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                    {row.hasDelta && row.delta !== null && Math.abs(row.delta) % 100 === 0 && (
                                        <Tooltip title="Potentially due to ACH or Options against this position" arrow placement="left">
                                            <WarningIcon sx={{ fontSize: 14, color: '#f59e0b', cursor: 'help' }} />
                                        </Tooltip>
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontFamily: row.remoteUnavailable ? 'inherit' : 'monospace',
                                            fontWeight: row.hasDelta ? 600 : 400,
                                            color: row.remoteUnavailable ? '#94a3b8' : (!row.hasDelta ? '#94a3b8' : ((row.delta ?? 0) > 0 ? '#059669' : '#dc2626')),
                                            fontStyle: row.remoteUnavailable ? 'italic' : 'normal',
                                            fontSize: row.remoteUnavailable ? '0.75rem' : 'inherit',
                                        }}
                                    >
                                        {row.remoteUnavailable ? 'N/A' : (!row.hasDelta ? '—' : ((row.delta ?? 0) > 0 ? '+' : '') + _.round(row.delta ?? 0, 6))}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>

            {/* Confirmation Dialog */}
            <Dialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    }
                }}
            >
                <DialogTitle sx={{ pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <WarningIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                            Are you sure?
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.6 }}>
                        This action should be used <strong>very sparingly</strong>. Recalculating positions on the fly can have unintended consequences and may affect the balancing engine's state.
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mt: 1.5, lineHeight: 1.6 }}>
                        Only proceed if you have verified there is an actual discrepancy that needs correction.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                    <Button
                        onClick={() => setShowConfirmDialog(false)}
                        variant="outlined"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            color: '#64748b',
                            borderColor: '#cbd5e1',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSyncAndRecalculate}
                        variant="contained"
                        size="small"
                        sx={{
                            textTransform: 'none',
                            bgcolor: '#f59e0b',
                            '&:hover': { bgcolor: '#d97706' },
                        }}
                    >
                        Yes, Sync & Recalculate
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Balancing Activities Modal
interface BalancingActivitiesModalProps {
    open: boolean;
    onClose: () => void;
    userKey: number;
    autoPilotSettingsKey: number;
}

const BalancingActivitiesModal: React.FC<BalancingActivitiesModalProps> = ({ open, onClose, userKey, autoPilotSettingsKey }) => {
    const [page, setPage] = useState(0);
    const [selectedActivity, setSelectedActivity] = useState<BalancingActivity | null>(null);
    const [ordersActivity, setOrdersActivity] = useState<BalancingActivity | null>(null);
    const pageSize = 20;

    const { data, loading, error } = useQueryAsUser(balancingActivitiesQuery, {
        userKey,
        variables: {
            autoPilotSettingsKey,
            limit: pageSize,
            offset: page * pageSize,
        },
        skip: !open,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
    });

    const activities: BalancingActivity[] = data?.autoPilotSettingsGet?.autoPilotSettings?.[0]?.autoPilotBalancingActivities ?? [];

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'EQUILIBRIUM_ACHIEVED': return { bg: '#ecfdf5', color: '#059669', label: 'Equilibrium' };
            case 'ORDERS_IN_FLIGHT': return { bg: '#eff6ff', color: '#3b82f6', label: 'In Flight' };
            case 'ORDERS_FAILED': return { bg: '#fef2f2', color: '#dc2626', label: 'Failed' };
            case 'ORDERS_PUBLISHED': return { bg: '#f5f3ff', color: '#7c3aed', label: 'Published' };
            case 'BALANCING_SUPPRESSED': return { bg: '#fefce8', color: '#ca8a04', label: 'Suppressed' };
            case 'PENDING_NEXT_CYCLE': return { bg: '#f1f5f9', color: '#64748b', label: 'Pending' };
            default: return { bg: '#f1f5f9', color: '#64748b', label: status || 'Unknown' };
        }
    };

    const formatCurrency = (value: number | null | undefined) => {
        if (value == null) return '—';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        return date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
            timeZoneName: 'short',
        });
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '85vh',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                    }
                }}
            >
                {/* Header */}
                <Box sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <HistoryIcon sx={{ fontSize: 22, color: '#475569' }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', color: '#1e293b' }}>
                                Balancing Activity History
                            </Typography>
                        </Box>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'grey.500' }}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.5 }}>
                        <Chip
                            label={`Page ${page + 1}`}
                            size="small"
                            sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 500 }}
                        />
                        {!loading && (
                            <Chip
                                label={`${activities.length} activities`}
                                size="small"
                                sx={{ bgcolor: '#e2e8f0', color: '#475569', fontWeight: 500 }}
                            />
                        )}
                    </Box>
                </Box>

                {/* Content */}
                <DialogContent sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                            <CircularProgress size={36} sx={{ color: '#3b82f6' }} />
                        </Box>
                    ) : error ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <ErrorIcon sx={{ fontSize: 40, color: '#ef4444', mb: 1 }} />
                            <Typography color="error" variant="body2">{error.message}</Typography>
                        </Box>
                    ) : activities.length === 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 200, gap: 1 }}>
                            <HistoryIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                            <Typography color="text.secondary" variant="body2">
                                No balancing activities found
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                            {/* Column Headers */}
                            <Box sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 120px 120px 100px 70px 50px',
                                gap: 2,
                                px: 3,
                                py: 1.5,
                                bgcolor: '#f8fafc',
                                borderBottom: '1px solid',
                                borderColor: 'grey.200',
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    Created At
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                    Slave Value
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                    Cash Position
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>
                                    Distance
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>
                                    Orders
                                </Typography>
                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>

                                </Typography>
                            </Box>

                            {activities.map((activity, index) => {
                                const statusInfo = getStatusColor(activity.balancingStatus);
                                const ordersCount = activity.autoPilotOrders?.length ?? 0;

                                return (
                                    <Box
                                        key={activity.autoPilotBalancingActivityKey || index}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 120px 120px 100px 70px 50px',
                                            gap: 2,
                                            px: 3,
                                            py: 1.5,
                                            borderBottom: '1px solid',
                                            borderColor: 'grey.100',
                                            alignItems: 'center',
                                            '&:hover': { bgcolor: '#f8fafc' },
                                            transition: 'background-color 0.15s',
                                        }}
                                    >
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Chip
                                                    label={statusInfo.label}
                                                    size="small"
                                                    sx={{
                                                        bgcolor: statusInfo.bg,
                                                        color: statusInfo.color,
                                                        fontWeight: 600,
                                                        fontSize: '0.65rem',
                                                        height: 20,
                                                    }}
                                                />
                                            </Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                                                {formatDate(activity.createdAt)}
                                            </Typography>
                                        </Box>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1e293b', textAlign: 'right', fontWeight: 500 }}>
                                            {formatCurrency(activity.slaveValuation)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1e293b', textAlign: 'right', fontWeight: 500 }}>
                                            {formatCurrency(activity.slaveCashPositionAmount)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#1e293b', textAlign: 'right' }}>
                                            {formatCurrency(activity.distanceToTargetPositions)}
                                        </Typography>
                                        <Box sx={{ textAlign: 'center' }}>
                                            {ordersCount > 0 ? (
                                                <Tooltip title={`View ${ordersCount} order${ordersCount !== 1 ? 's' : ''}`} arrow>
                                                    <Chip
                                                        icon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
                                                        label={ordersCount}
                                                        size="small"
                                                        onClick={() => setOrdersActivity(activity)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            bgcolor: '#eff6ff',
                                                            color: '#3b82f6',
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            '& .MuiChip-icon': { color: '#3b82f6' },
                                                            '&:hover': { bgcolor: '#dbeafe' },
                                                        }}
                                                    />
                                                </Tooltip>
                                            ) : (
                                                <Typography variant="caption" sx={{ color: '#cbd5e1' }}>—</Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Tooltip title="View raw JSON data" arrow>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setSelectedActivity(activity)}
                                                    sx={{ color: '#94a3b8' }}
                                                >
                                                    <DataObjectIcon sx={{ fontSize: 16 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </DialogContent>

                {/* Pagination */}
                <Box sx={{
                    px: 3,
                    py: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#f8fafc',
                }}>
                    <Button
                        size="small"
                        startIcon={<NavigateBeforeIcon />}
                        onClick={() => setPage(p => Math.max(0, p - 1))}
                        disabled={page === 0 || loading}
                        sx={{ textTransform: 'none' }}
                    >
                        Previous
                    </Button>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                        Showing {page * pageSize + 1} - {page * pageSize + activities.length}
                    </Typography>
                    <Button
                        size="small"
                        endIcon={<NavigateNextIcon />}
                        onClick={() => setPage(p => p + 1)}
                        disabled={activities.length < pageSize || loading}
                        sx={{ textTransform: 'none' }}
                    >
                        Next
                    </Button>
                </Box>
            </Dialog>

            {/* Raw JSON Data Modal */}
            <Dialog
                open={!!selectedActivity}
                onClose={() => setSelectedActivity(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '85vh',
                    }
                }}
            >
                <Box sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#1e293b',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DataObjectIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
                            Raw Activity Data
                        </Typography>
                        {selectedActivity && (
                            <Chip
                                label={`Key: ${selectedActivity.autoPilotBalancingActivityKey}`}
                                size="small"
                                sx={{ bgcolor: '#334155', color: '#94a3b8', fontSize: '0.7rem' }}
                            />
                        )}
                    </Box>
                    <IconButton onClick={() => setSelectedActivity(null)} size="small" sx={{ color: '#94a3b8' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 0 }}>
                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 2,
                            bgcolor: '#0f172a',
                            color: '#e2e8f0',
                            fontSize: '0.75rem',
                            fontFamily: 'Monaco, Consolas, monospace',
                            overflow: 'auto',
                            maxHeight: '60vh',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                        }}
                    >
                        {selectedActivity ? JSON.stringify(selectedActivity, null, 2) : ''}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Orders Modal for selected activity */}
            <Dialog
                open={!!ordersActivity}
                onClose={() => setOrdersActivity(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 2,
                        maxHeight: '85vh',
                    }
                }}
            >
                <Box sx={{
                    px: 3,
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <ReceiptLongIcon sx={{ fontSize: 22, color: 'white' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', fontSize: '1.1rem' }}>
                            Activity Orders
                        </Typography>
                        {ordersActivity && (
                            <Chip
                                label={`${ordersActivity.autoPilotOrders?.length ?? 0} orders`}
                                size="small"
                                sx={{ bgcolor: '#475569', color: 'white', fontWeight: 500 }}
                            />
                        )}
                    </Box>
                    <IconButton onClick={() => setOrdersActivity(null)} size="small" sx={{ color: 'white' }}>
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
                <DialogContent sx={{ p: 0, bgcolor: '#f8fafc' }}>
                    {ordersActivity?.autoPilotOrders && ordersActivity.autoPilotOrders.length > 0 ? (
                        <Box sx={{ p: 2 }}>
                            {ordersActivity.autoPilotOrders.map((order, idx) => {
                                const isBuy = order.orderSide === 'BUY';
                                const getOrderStatusColor = (status: string) => {
                                    switch (status?.toUpperCase()) {
                                        case 'FULFILLED': return { bg: '#f0fdf4', color: '#10b981', border: '#bbf7d0' };
                                        case 'FAILED': return { bg: '#fef2f2', color: '#ef4444', border: '#fecaca' };
                                        case 'PENDING': return { bg: '#fef3c7', color: '#d97706', border: '#fcd34d' };
                                        case 'CANCELLED': return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
                                        default: return { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
                                    }
                                };
                                const statusColors = getOrderStatusColor(order.orderStatus);

                                return (
                                    <Box
                                        key={idx}
                                        sx={{
                                            mb: 1.5,
                                            p: 2,
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            border: '1px solid',
                                            borderColor: 'grey.200',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                                            {/* Symbol */}
                                            <Typography variant="body1" sx={{ fontWeight: 700, minWidth: 80 }}>
                                                {order.symbol}
                                            </Typography>

                                            {/* Order Side */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 70 }}>
                                                {isBuy ? (
                                                    <TrendingUpOutlinedIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                                ) : (
                                                    <TrendingDownOutlinedIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                )}
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: isBuy ? '#10b981' : '#ef4444' }}>
                                                    {order.orderSide}
                                                </Typography>
                                            </Box>

                                            {/* Position Type */}
                                            <Chip
                                                label={order.positionType}
                                                size="small"
                                                sx={{ bgcolor: '#f1f5f9', color: '#475569', fontSize: '0.7rem' }}
                                            />

                                            {/* Position Effect */}
                                            {order.positionEffect && (
                                                <Chip
                                                    label={order.positionEffect}
                                                    size="small"
                                                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontSize: '0.7rem' }}
                                                />
                                            )}

                                            {/* Amount */}
                                            <Box sx={{ minWidth: 100 }}>
                                                <Typography variant="caption" color="text.secondary">Amount</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                    {formatCurrency(order.transactionAmount)}
                                                </Typography>
                                            </Box>

                                            {/* Quantity */}
                                            <Box sx={{ minWidth: 80 }}>
                                                <Typography variant="caption" color="text.secondary">Qty</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {(order.quantity || 0).toFixed(6)}
                                                </Typography>
                                            </Box>

                                            {/* Balancing Decision Quantity */}
                                            <Box sx={{ minWidth: 100 }}>
                                                <Typography variant="caption" color="text.secondary">Bal Dec Qty</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {(order.balancingDecisionQuantity || 0).toFixed(6)}
                                                </Typography>
                                            </Box>

                                            {/* Avg Price */}
                                            <Box sx={{ minWidth: 90 }}>
                                                <Typography variant="caption" color="text.secondary">Avg Price</Typography>
                                                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                                    {formatCurrency(order.averagePrice)}
                                                </Typography>
                                            </Box>

                                            {/* Status */}
                                            <Chip
                                                label={order.orderStatus}
                                                size="small"
                                                sx={{
                                                    bgcolor: statusColors.bg,
                                                    color: statusColors.color,
                                                    border: `1px solid ${statusColors.border}`,
                                                    fontWeight: 600,
                                                    fontSize: '0.7rem',
                                                }}
                                            />

                                            {/* State */}
                                            <Typography variant="caption" color="text.secondary">
                                                {order.orderState}
                                            </Typography>

                                            {/* Created At */}
                                            <Box sx={{ ml: 'auto' }}>
                                                <Typography variant="caption" color="text.secondary">Created</Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'grey.600' }}>
                                                    {order.createdAt ? new Date(order.createdAt).toLocaleString(undefined, { timeZoneName: 'short' }) : '—'}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Failure Reason */}
                                        {order.failureReason && (
                                            <Box sx={{ mt: 1.5, p: 1.5, bgcolor: '#fef2f2', borderRadius: 1, border: '1px solid #fecaca' }}>
                                                <Typography variant="caption" sx={{ color: '#dc2626', fontWeight: 600 }}>
                                                    Failure Reason:
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#991b1b', mt: 0.5 }}>
                                                    {typeof order.failureReason === 'object' ? JSON.stringify(order.failureReason) : order.failureReason}
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Fulfilled At */}
                                        {order.fullfilledAt && (
                                            <Typography variant="caption" sx={{ color: '#059669', mt: 1, display: 'block' }}>
                                                Fulfilled: {new Date(order.fullfilledAt).toLocaleString(undefined, { timeZoneName: 'short' })}
                                            </Typography>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 200, gap: 1 }}>
                            <ReceiptLongIcon sx={{ fontSize: 40, color: '#cbd5e1' }} />
                            <Typography color="text.secondary" variant="body2">
                                No orders for this activity
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export const BalancingDecisionsTableV2: React.FC<Props> = ({ userKey, autoPilotSettingsKey }) => {
    const { data, loading, error, refetch } = useQueryAsUser(balancingConnectionQuery, {
        userKey,
        variables: { autoPilotSettingsKey },
        errorPolicy: 'all',
        returnPartialData: true,
    });

    // Reuses the trigger-history query that TriggerHistoryTable already runs on this page.
    // cache-first so it shares Apollo's cached payload instead of triggering another network call.
    const { data: triggerHistoryData } = useQueryAsUser(pilotTriggerHistoryQuery, {
        userKey,
        variables: { userKey, autoPilotSettingsKey, limit: 100, offset: 0 },
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
    });
    const triggerHistory: TriggerHistoryItem[] = triggerHistoryData?.triggerHistoryGet ?? [];

    const freshAutoPilotSettings = data?.autoPilotSettingsGet?.autoPilotSettings?.[0];
    const freshBalancingConnection = freshAutoPilotSettings?.balancingConnection;
    const freshDecisions: BalancingDecisionV2[] = freshBalancingConnection?.decisions ?? [];

    // The activities modal's query writes to the shared `autoPilotSettings` cache entity.
    // With returnPartialData, that can briefly null out balancingConnection / empty decisions,
    // tripping the early returns below and unmounting the modal subtree (state lost -> flap).
    // Cache the last good payload and render from it whenever the live data is incomplete.
    const lastGoodRef = useRef<{
        autoPilotSettings: typeof freshAutoPilotSettings;
        balancingConnection: typeof freshBalancingConnection;
        decisions: BalancingDecisionV2[];
    } | null>(null);
    if (freshBalancingConnection && freshDecisions.length > 0) {
        lastGoodRef.current = {
            autoPilotSettings: freshAutoPilotSettings,
            balancingConnection: freshBalancingConnection,
            decisions: freshDecisions,
        };
    }
    const autoPilotSettings = freshAutoPilotSettings ?? lastGoodRef.current?.autoPilotSettings;
    const balancingConnection = freshBalancingConnection ?? lastGoodRef.current?.balancingConnection;
    const setupStatus = autoPilotSettings?.setupStatus;
    const decisions: BalancingDecisionV2[] =
        freshDecisions.length > 0 ? freshDecisions : (lastGoodRef.current?.decisions ?? []);
    const decisionContext: DecisionContext | null = balancingConnection?.decisionContext ?? null;
    const overAllocationAmount: number = balancingConnection?.overAllocationAmount ?? 0;
    const hasOverAllocation = overAllocationAmount > 0;
    const [selectedDecision, setSelectedDecision] = useState<any>(null);
    const [rawDataOpen, setRawDataOpen] = useState(false);
    const [contextModalOpen, setContextModalOpen] = useState(false);
    const [ordersModalOpen, setOrdersModalOpen] = useState(false);
    const [positionsModalOpen, setPositionsModalOpen] = useState(false);
    const [activitiesModalOpen, setActivitiesModalOpen] = useState(false);
    const [resolveOpen, setResolveOpen] = useState(false);
    const [resolveConfirmText, setResolveConfirmText] = useState('');
    const [resolveOverAllocationMutation, { loading: resolving }] = useMutationCoordinate(jediResolveOverAllocation, {});

    const handleResolveOverAllocation = async () => {
        if (resolveConfirmText.trim() !== 'danistheman') return;
        try {
            await resolveOverAllocationMutation({ variables: { autoPilotSettingsKey } });
            setResolveOpen(false);
            setResolveConfirmText('');
            await refetch();
        } catch (e) {
            console.error('Failed to resolve over-allocation:', e);
        }
    };

    if (loading && !balancingConnection) {
        return (
            <Paper elevation={0} sx={{
                width: '100%',
                p: 4,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 400,
                bgcolor: 'grey.50',
                borderRadius: 3,
            }}>
                <CircularProgress size={40} />
            </Paper>
        );
    }

    // Treat GraphQL field-level errors as non-fatal when we still received usable payload.
    if (error && !balancingConnection) {
        return (
            <Paper elevation={0} sx={{
                width: '100%',
                p: 4,
                bgcolor: '#fef2f2',
                borderRadius: 3,
                border: '1px solid #fecaca'
            }}>
                <Typography color="error" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorIcon /> Error: {error.message}
                </Typography>
            </Paper>
        );
    }

    if (!decisions || decisions.length === 0) {
        if (setupStatus === 'DELETED') {
            return (
                <Paper elevation={0} sx={{
                    width: '100%',
                    p: 4,
                    bgcolor: '#f0fdf4',
                    borderRadius: 3,
                    textAlign: 'center',
                    border: '1px solid #bbf7d0',
                }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 1 }} />
                    <Typography variant="h6" sx={{ color: '#059669', fontWeight: 600 }}>
                        Deleted Successfully!
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#047857', mt: 0.5 }}>
                        This Pilot has been deleted.
                    </Typography>
                </Paper>
            );
        }
        return (
            <Paper elevation={0} sx={{
                width: '100%',
                p: 4,
                bgcolor: 'grey.50',
                borderRadius: 3,
                textAlign: 'center'
            }}>
                <Typography color="text.secondary">No balancing decisions available</Typography>
            </Paper>
        );
    }

    const columns: GridColDef[] = [
        {
            field: 'kind',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => <DecisionIcon kind={params.value} />
        },
        {
            field: 'workRequiredAttributionState',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => <AttributionIcon state={params.value} />
        },
        {
            field: 'symbol',
            headerName: 'Symbol',
            width: 120,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StockSymbolComponent logo={params.row.pictureUrl} symbol={params.row.symbol} value={params.value} />
                </Box>
            )
        },
        {
            field: 'orderSide',
            headerName: 'Action',
            width: 140,
            renderCell: (params: GridRenderCellParams) => {
                const isNoop = params.row.kind === 'INoopBalancingDecision';
                if (isNoop) {
                    return <Typography variant="body2" color="text.secondary">—</Typography>;
                }
                const isBuy = params.value === 'BUY';
                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: isBuy ? '#10b981' : '#ef4444',
                        fontWeight: 600,
                    }}>
                        {isBuy ? <TrendingUpOutlinedIcon sx={{ fontSize: 18 }} /> : <TrendingDownOutlinedIcon sx={{ fontSize: 18 }} />}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {params.value}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'price',
            headerName: 'Price',
            width: 100,
            type: 'number',
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {(params.value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                </Typography>
            )
        },
        {
            field: 'currentQuantity',
            headerName: 'Current Qty',
            width: 160,
            type: 'number',
            renderCell: (params) => {
                const currentMarketValue = (params.value || 0) * (params.row.price || 0);
                const marketValue = params.row.marketValue || 0;
                const percentage = marketValue > 0 ? (currentMarketValue / marketValue) * 100 : 0;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'grey.500', fontSize: '0.75rem' }}>
                            ({percentage.toFixed(2)}%)
                        </Typography>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {_.round(params.value, 6)}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            field: 'targetQuantity',
            headerName: 'Target Qty',
            width: 120,
            type: 'number',
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                    {_.round(params.value, 6)}
                </Typography>
            )
        },
        {
            field: 'quantityToExecute',
            headerName: 'Delta Qty',
            width: 120,
            type: 'number',
            renderCell: (params) => {
                const isNoop = params.row.kind === 'INoopBalancingDecision';
                if (isNoop) return <Typography variant="body2" color="text.secondary">—</Typography>;
                const isBuy = params.row.orderSide === 'BUY';
                return (
                    <Typography variant="body2" sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: isBuy ? '#10b981' : '#ef4444',
                        fontWeight: 600,
                    }}>
                        {isBuy ? '+' : '-'}{_.round(Math.abs(params.value || 0), 6)}
                    </Typography>
                );
            }
        },
        {
            field: 'amountToExecute',
            headerName: 'Amount',
            width: 140,
            type: 'number',
            renderCell: (params) => {
                const isNoop = params.row.kind === 'INoopBalancingDecision';
                if (isNoop) return <Typography variant="body2" color="text.secondary">—</Typography>;
                const isBuy = params.row.orderSide === 'BUY';
                return (
                    <Typography variant="body2" sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: isBuy ? '#10b981' : '#ef4444',
                        fontWeight: 600,
                    }}>
                        {(params.value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </Typography>
                );
            }
        },
        {
            field: 'buyingPower',
            headerName: 'Buying Power',
            width: 140,
            type: 'number',
            renderCell: (params: GridRenderCellParams) => {
                if (params.value == null) return <Typography variant="body2" color="text.secondary">—</Typography>;
                return (
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                        {(params.value || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </Typography>
                );
            }
        },
        {
            field: 'rulesInfo',
            headerName: 'Rules',
            width: 120,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const passed = params.row.rulesPassed?.length || 0;
                const failed = params.row.rulesFailed?.length || 0;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {failed > 0 && (
                            <Chip
                                label={failed}
                                size="small"
                                sx={{
                                    height: 22,
                                    minWidth: 22,
                                    bgcolor: '#fef2f2',
                                    color: '#ef4444',
                                    fontWeight: 600,
                                    '& .MuiChip-label': { px: 0.75 }
                                }}
                            />
                        )}
                        <Chip
                            label={passed}
                            size="small"
                            sx={{
                                height: 22,
                                minWidth: 22,
                                bgcolor: '#f0fdf4',
                                color: '#10b981',
                                fontWeight: 600,
                                '& .MuiChip-label': { px: 0.75 }
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            field: 'modifications',
            headerName: 'Mods',
            width: 100,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const mods = params.row.targetPosition?.modifications || [];
                const appliedCount = mods.filter((m: any) => m.modified).length;
                if (mods.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
                return (
                    <Chip
                        label={`${appliedCount}/${mods.length}`}
                        size="small"
                        sx={{
                            height: 22,
                            bgcolor: appliedCount > 0 ? '#dbeafe' : '#f3f4f6',
                            color: appliedCount > 0 ? '#1d4ed8' : '#6b7280',
                            fontWeight: 600,
                            '& .MuiChip-label': { px: 0.75 }
                        }}
                    />
                );
            }
        },
    ];

    // Sort priority: NO_WORK_REQUIRED should be last
    const attributionSortOrder: Record<string, number> = {
        'PLATFORM': 0,
        'BROKER': 1,
        'USER': 2,
        'NO_WORK_REQUIRED': 3,
    };

    // Calculate market value before rows so it can be included in each row
    const totalPositionMarketValue = _.sumBy(decisions, d => (d.currentQuantity ?? 0) * (d.price ?? 0));
    const marketValue = totalPositionMarketValue + (decisionContext?.snapshot?.cash ?? 0);

    // Per-asset buying power keyed by symbol, surfaced as a column on each target position.
    // Entries are nullable in the schema, so guard against null elements / fields.
    const buyingPowerBySymbol: Record<string, number> = {};
    for (const entry of decisionContext?.assetBuyingPowerData ?? []) {
        if (entry?.symbol == null || entry.buyingPower == null) continue;
        buyingPowerBySymbol[entry.symbol] = entry.buyingPower;
    }

    const rows = _.orderBy(decisions.map((decision: BalancingDecisionV2) => {
        const symbol = decision.targetPosition?.symbol;
        return {
            id: decision.targetPosition?.symbol || decision.assetKey,
            symbol: decision.targetPosition?.symbol,
            positionType: decision.positionType,
            orderSide: decision.orderSide,
            kind: decision.kind,
            workRequiredAttributionState: decision.workRequiredAttributionState,
            attributionSortKey: attributionSortOrder[decision.workRequiredAttributionState] ?? 99,
            currentQuantity: decision.currentQuantity,
            targetQuantity: decision.targetQuantity,
            quantityToExecute: decision.quantityToExecute,
            amountToExecute: decision.amountToExecute,
            price: decision.price,
            buyingPower: symbol != null ? buyingPowerBySymbol[symbol] : undefined,
            rulesPassed: decision.rulesPassed,
            rulesFailed: decision.rulesFailed,
            targetPosition: decision.targetPosition,
            pictureUrl: decision.targetPosition?.asset?.pictureUrl,
            marketValue: marketValue,
        };
    }), ['kind', 'attributionSortKey', 'amountToExecute'], ['asc', 'asc', 'desc']);

    const equilibriumRules = ['AssetToleranceRule', 'TargetPositionFulfilledRule'];
    const isEquilibriumNoop = (r: typeof rows[0]): boolean =>
        r.kind === 'INoopBalancingDecision' && (
            r.workRequiredAttributionState === 'NO_WORK_REQUIRED' ||
            r.rulesFailed?.some(rule => equilibriumRules.includes(rule.type))
        );
    const executeDecisions = rows.filter(r => r.kind !== 'INoopBalancingDecision');
    // Noop decisions that are blocked (not equilibrium)
    const blockedNoopDecisions = rows.filter(r =>
        r.kind === 'INoopBalancingDecision' && !isEquilibriumNoop(r)
    );
    // True equilibrium = no execute decisions AND no blocked noop decisions
    const isInEquilibrium = executeDecisions.length === 0 && blockedNoopDecisions.length === 0;

    const totalBuyAmount = _.sumBy(executeDecisions.filter(r => r.orderSide === 'BUY'), 'amountToExecute') || 0;
    const totalSellAmount = _.sumBy(executeDecisions.filter(r => r.orderSide === 'SELL'), 'amountToExecute') || 0;
    const netAmount = totalBuyAmount - totalSellAmount;

    // Group blocked decisions by attribution state
    const blockedByState = _.groupBy(blockedNoopDecisions, 'workRequiredAttributionState');

    return (
        <Paper elevation={0} sx={{
            width: '100%',
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'grey.200',
            overflow: 'hidden',
        }}>
            {/* Header */}
            <Box sx={{
                p: 2.5,
                borderBottom: '1px solid',
                borderColor: 'grey.100',
                background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)',
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        {decisions.length} positions • {executeDecisions.length} pending actions{blockedNoopDecisions.length > 0 ? ` • ${blockedNoopDecisions.length} blocked` : ''}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {hasOverAllocation && (
                            <Tooltip title={`Over-allocated by ${overAllocationAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}. Click to resolve.`}>
                                <Button
                                    variant="outlined"
                                    color="warning"
                                    size="small"
                                    startIcon={<BalanceOutlinedIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => { setResolveConfirmText(''); setResolveOpen(true); }}
                                    sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', bgcolor: 'white' }}
                                >
                                    Resolve ({overAllocationAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })})
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title="View Orders">
                            <IconButton size="small" onClick={() => setOrdersModalOpen(true)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                                <ReceiptLongIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        {decisionContext?.snapshot?.portfolioKey && (
                            <Tooltip title="View Remote Positions">
                                <IconButton size="small" onClick={() => setPositionsModalOpen(true)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                                    <StorageIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="View Balancing Activity History">
                            <IconButton size="small" onClick={() => setActivitiesModalOpen(true)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                                <HistoryIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                        {decisionContext && (
                            <Tooltip title="View Context Details">
                                <IconButton size="small" onClick={() => setContextModalOpen(true)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                                    <SettingsIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <Tooltip title="View Raw JSON">
                            <IconButton size="small" onClick={() => setRawDataOpen(true)} sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                                <InfoIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Equilibrium State, Blocked Actions, or Buy/Sell/Net Stats */}
                {isInEquilibrium ? (
                    <Box
                        sx={{
                            p: 1.5,
                            background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                            borderRadius: 2,
                            border: '1px solid #bbf7d0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        <BalanceIcon sx={{ color: '#10b981', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#166534' }}>
                            Portfolio in Equilibrium
                        </Typography>
                    </Box>
                ) : executeDecisions.length === 0 && blockedNoopDecisions.length > 0 ? (
                    <Box sx={{ mb: 2 }}>
                        <Box
                            sx={{
                                p: 1.5,
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                borderRadius: 2,
                                border: '1px solid #fcd34d',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WarningIcon sx={{ color: '#d97706', fontSize: 20 }} />
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#92400e' }}>
                                    {blockedNoopDecisions.length} Blocked Action{blockedNoopDecisions.length > 1 ? 's' : ''}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1.5 }}>
                                {blockedByState['BROKER'] && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccountBalanceIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1d4ed8' }}>
                                            {blockedByState['BROKER'].length} Broker
                                        </Typography>
                                    </Box>
                                )}
                                {blockedByState['USER'] && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <PersonIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#b45309' }}>
                                            {blockedByState['USER'].length} User
                                        </Typography>
                                    </Box>
                                )}
                                {blockedByState['PLATFORM'] && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <BuildIcon sx={{ fontSize: 16, color: '#ef4444' }} />
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#b91c1c' }}>
                                            {blockedByState['PLATFORM'].length} Platform
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: '#f0fdf4',
                                borderRadius: 2,
                                border: '1px solid #bbf7d0',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                            }}>
                                <TrendingUpOutlinedIcon sx={{ color: '#10b981', fontSize: 28 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Total Buy</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
                                        {totalBuyAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: '#fef2f2',
                                borderRadius: 2,
                                border: '1px solid #fecaca',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5
                            }}>
                                <TrendingDownOutlinedIcon sx={{ color: '#ef4444', fontSize: 28 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Total Sell</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444', fontFamily: 'monospace' }}>
                                        {totalSellAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{
                                flex: 1,
                                p: 2,
                                bgcolor: netAmount >= 0 ? '#f0fdf4' : '#fef2f2',
                                borderRadius: 2,
                                border: '1px solid',
                                borderColor: netAmount >= 0 ? '#bbf7d0' : '#fecaca',
                            }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Net Flow</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: netAmount >= 0 ? '#10b981' : '#ef4444', fontFamily: 'monospace' }}>
                                    {netAmount >= 0 ? '+' : ''}{netAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                </Typography>
                            </Box>
                        </Box>
                        {blockedNoopDecisions.length > 0 && (
                            <Box
                                sx={{
                                    mt: 1.5,
                                    p: 1,
                                    bgcolor: '#fef3c7',
                                    borderRadius: 1.5,
                                    border: '1px solid #fcd34d',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                    <WarningIcon sx={{ color: '#d97706', fontSize: 16 }} />
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: '#92400e' }}>
                                        {blockedNoopDecisions.length} Blocked
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1.5 }}>
                                    {blockedByState['BROKER'] && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccountBalanceIcon sx={{ fontSize: 14, color: '#3b82f6' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#1d4ed8' }}>
                                                {blockedByState['BROKER'].length}
                                            </Typography>
                                        </Box>
                                    )}
                                    {blockedByState['USER'] && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: 14, color: '#f59e0b' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#b45309' }}>
                                                {blockedByState['USER'].length}
                                            </Typography>
                                        </Box>
                                    )}
                                    {blockedByState['PLATFORM'] && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <BuildIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 500, color: '#b91c1c' }}>
                                                {blockedByState['PLATFORM'].length}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Subtle Account Stats */}
                <Box sx={{
                    display: 'flex',
                    gap: 3,
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'grey.200',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 500 }}>Position Market Value</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700', fontFamily: 'monospace' }}>
                            {totalPositionMarketValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 500 }}>AP Cash</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700', fontFamily: 'monospace' }}>
                            {(decisionContext?.snapshot?.cash ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 500 }}>Total Market Value</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'grey.700', fontFamily: 'monospace' }}>
                            {marketValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                        <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 500 }}>Funds Available</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#10b981', fontFamily: 'monospace' }}>
                            {(decisionContext?.fundsAvailable ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Typography>
                    </Box>
                    {decisionContext && (
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                            <Typography variant="caption" sx={{ color: 'grey.500', fontWeight: 500 }}>Realized P/L</Typography>
                            <Typography variant="body2" sx={{
                                fontWeight: 600,
                                color: (decisionContext.snapshot?.realizedProfitLoss ?? 0) >= 0 ? '#10b981' : '#ef4444',
                                fontFamily: 'monospace'
                            }}>
                                {(decisionContext.snapshot?.realizedProfitLoss ?? 0) >= 0 ? '+' : ''}{(decisionContext.snapshot?.realizedProfitLoss ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Data Grid */}
            <Box sx={{ height: Math.min(Math.max(rows.length * 52 + 56, 200), 600) }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    rowHeight={52}
                    hideFooter
                    disableColumnMenu
                    onRowClick={(params) => setSelectedDecision(params.row)}
                    sx={{
                        border: 'none',
                        height: '100%',
                    '& .MuiDataGrid-row': {
                        cursor: 'pointer',
                        '&:hover': {
                            bgcolor: 'grey.50',
                        },
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        bgcolor: 'grey.50',
                        borderBottom: '1px solid',
                        borderColor: 'grey.200',
                    },
                    '& .MuiDataGrid-columnHeaderTitle': {
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        color: 'grey.700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.03em',
                    },
                    '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid',
                        borderColor: 'grey.100',
                    },
                }}
                />
            </Box>

            {/* Detail Modal */}
            <DetailModal
                open={!!selectedDecision}
                onClose={() => setSelectedDecision(null)}
                decision={selectedDecision}
                userKey={userKey}
                autoPilotSettingsKey={autoPilotSettingsKey}
                triggerHistory={triggerHistory}
            />

            {/* Resolve Over-Allocation Modal */}
            <Dialog
                open={resolveOpen}
                onClose={() => (resolving ? undefined : setResolveOpen(false))}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                    <BalanceOutlinedIcon sx={{ color: '#d97706' }} />
                    Resolve over-allocation?
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        This will reduce the pilot&apos;s allocation by{' '}
                        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                            {overAllocationAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Box>{' '}
                        to clear the over-allocation. Enter the password to proceed.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        type="password"
                        label="Password"
                        value={resolveConfirmText}
                        onChange={(e) => setResolveConfirmText(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleResolveOverAllocation(); }}
                        disabled={resolving}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setResolveOpen(false)} disabled={resolving}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleResolveOverAllocation}
                        disabled={resolveConfirmText.trim() !== 'danistheman' || resolving}
                        startIcon={resolving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    >
                        {resolving ? 'Resolving...' : 'Resolve'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Context Details Modal */}
            <Dialog
                open={contextModalOpen}
                onClose={() => setContextModalOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'grey.200' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Balancing Context</Typography>
                    <IconButton onClick={() => setContextModalOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    {decisionContext && (
                        <>
                            {/* Portfolio Stats */}
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'grey.700' }}>Portfolio</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">AutoPilot Cash</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                        {(decisionContext.snapshot?.cash ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Valuation Ratio</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                        {((decisionContext.snapshot?.valuationRatio ?? 0) * 100).toFixed(4)}%
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Funds */}
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'grey.700' }}>Funds</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
                                <Box sx={{ p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                                    <Typography variant="caption" color="text.secondary">Funds Available</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981', fontFamily: 'monospace' }}>
                                        {(decisionContext.fundsAvailable ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Unsettled Funds</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                        {(decisionContext.unsettledFunds ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Sub-Dollar Min</Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                        {(decisionContext.subDollarMinOrderRequirement ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Settings */}
                            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'grey.700' }}>Settings</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mb: 3 }}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Balancing Engine</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{decisionContext.snapshot?.type}</Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Broker</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{decisionContext.snapshot?.brokerName}</Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Approval Type</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{decisionContext.approvalType}</Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                    <Typography variant="caption" color="text.secondary">Blacklisted Symbols</Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{decisionContext.snapshot?.symbolBlacklist?.length ?? 0}</Typography>
                                    {(decisionContext.snapshot?.symbolBlacklist?.length ?? 0) > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                            {decisionContext.snapshot?.symbolBlacklist?.slice(0, 5).join(', ')}
                                            {(decisionContext.snapshot?.symbolBlacklist?.length ?? 0) > 5 && '...'}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Failure Analysis */}
                            {(decisionContext.failureAnalysis?.filter(fa => fa.data?.failureAttribution && fa.data.failureAttribution.length > 0).length ?? 0) > 0 && (
                                <>
                                    <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: '#ef4444' }}>
                                        Failed Assets ({decisionContext.failureAnalysis?.filter(fa => fa.data?.failureAttribution && fa.data.failureAttribution.length > 0).length})
                                    </Typography>
                                    <List dense sx={{ bgcolor: '#fef2f2', borderRadius: 2, mb: 2 }}>
                                        {decisionContext.failureAnalysis?.filter(fa => fa.data?.failureAttribution && fa.data.failureAttribution.length > 0).map((fa, idx) => (
                                            <ListItem key={idx}>
                                                <ListItemIcon sx={{ minWidth: 36 }}>
                                                    <ErrorIcon sx={{ color: '#ef4444', fontSize: 20 }} />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={fa.data?.failureAttribution?.[0]?.symbol || `Asset ${fa.assetKey}`}
                                                    secondary={fa.data?.failureAttribution?.[0]?.errorMessage}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Raw Data Dialog */}
            <Dialog
                open={rawDataOpen}
                onClose={() => setRawDataOpen(false)}
                maxWidth="lg"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Raw Balancing Data</Typography>
                    <IconButton onClick={() => setRawDataOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Decision Context</Typography>
                    <Box component="pre" sx={{
                        bgcolor: 'grey.900',
                        color: '#60a5fa',
                        p: 2,
                        borderRadius: 2,
                        overflow: 'auto',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        maxHeight: '30vh',
                        mb: 2,
                    }}>
                        {JSON.stringify(decisionContext, null, 2)}
                    </Box>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Decisions ({decisions.length})</Typography>
                    <Box component="pre" sx={{
                        bgcolor: 'grey.900',
                        color: '#10b981',
                        p: 2,
                        borderRadius: 2,
                        overflow: 'auto',
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                        maxHeight: '30vh',
                    }}>
                        {JSON.stringify(decisions, null, 2)}
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Orders Modal */}
            <OrdersModal
                open={ordersModalOpen}
                onClose={() => setOrdersModalOpen(false)}
                userKey={userKey}
                autoPilotSettingsKey={autoPilotSettingsKey}
            />

            {/* Remote Positions Modal */}
            {decisionContext?.snapshot?.portfolioKey && (
                <RemotePositionsModal
                    open={positionsModalOpen}
                    onClose={() => setPositionsModalOpen(false)}
                    userKey={userKey}
                    portfolioKey={decisionContext.snapshot.portfolioKey}
                />
            )}
            <BalancingActivitiesModal
                open={activitiesModalOpen}
                onClose={() => setActivitiesModalOpen(false)}
                userKey={userKey}
                autoPilotSettingsKey={autoPilotSettingsKey}
            />
        </Paper>
    );
};
